import { Component, forwardRef, Input, inject, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PhoneService } from '@services/shared/phone.service';
import { SharedModule } from '@shared/modules/shared.module';
import { Subject, takeUntil } from 'rxjs';
import { InputComponent } from '../input/input.component';
import { SelectComponent } from '../select/select.component';

@Component({
	selector: 'app-phone',
	imports: [SharedModule, SelectComponent, InputComponent],
	templateUrl: './phone.component.html',
	styleUrl: './phone.component.scss',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => PhoneComponent),
			multi: true,
		},
	],
})
export class PhoneComponent implements ControlValueAccessor, OnInit, OnDestroy {
	@Input() label = '';
	@Input() placeholder = '000 000 000';
	@Input() size: 'large' | 'medium' | 'small' = 'medium';
	@Input() disabled = false;
	@Input() error = false;
	@Input() errorMessage = '';

	// Internal FormControls
	prefixControl = new FormControl('+34');
	customPrefixControl = new FormControl('');
	phoneControl = new FormControl('');

	showCustomPrefix = false;

	countryCodeOptions = [
		{ label: '+34', value: '+34', icon: 'assets/layout/images/esp.png' },
		{ label: '+376', value: '+376', icon: 'assets/layout/images/and.png' },
		{ label: 'Other', value: 'other', icon: '' },
	];

	private phoneService = inject(PhoneService);
	private onChange: (value: string | null) => void = () => {};
	private onTouched: () => void = () => {};
	private destroy$ = new Subject<void>();

	ngOnInit(): void {
		this.setupPhoneFormatting();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private setupPhoneFormatting(): void {
		// Format phone based on prefix
		this.phoneControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
			if (value && typeof value === 'string') {
				const prefix = this.displayPhoneCode;
				const formatted = this.phoneService.formatNumber(value, prefix);
				if (formatted !== value) {
					this.phoneControl.setValue(formatted, { emitEvent: false });
				}
				this.emitValue();
			} else {
				this.emitValue();
			}
		});

		// Reformat phone when prefix changes
		this.prefixControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((prefix) => {
			this.showCustomPrefix = prefix === 'other';
			const phoneValue = this.phoneControl.value;
			if (phoneValue) {
				const formatted = this.phoneService.formatNumber(phoneValue, this.displayPhoneCode);
				this.phoneControl.setValue(formatted, { emitEvent: false });
			}
			this.emitValue();
		});

		// Format custom prefix
		this.customPrefixControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
			if (value && typeof value === 'string') {
				const formatted = this.phoneService.formatPhoneCode(value);
				if (formatted !== value) {
					this.customPrefixControl.setValue(formatted, { emitEvent: false });
				}
				// Reformat phone with new prefix
				const phoneValue = this.phoneControl.value;
				if (phoneValue) {
					const formattedPhone = this.phoneService.formatNumber(phoneValue, this.displayPhoneCode);
					this.phoneControl.setValue(formattedPhone, { emitEvent: false });
				}
				this.emitValue();
			}
		});
	}

	get displayPhoneCode(): string {
		const prefix = this.prefixControl.value;
		if (prefix === 'other') {
			return this.customPrefixControl.value || '';
		}
		return prefix || '+34';
	}

	private emitValue(): void {
		const prefix = this.displayPhoneCode;
		const number = (this.phoneControl.value || '').replace(/\s/g, '');

		if (!number) {
			this.onChange(null);
			return;
		}

		// Format: prefix|number (e.g., "+34|612345678")
		this.onChange(`${prefix}|${number}`);
	}

	// ControlValueAccessor implementation
	writeValue(value: string | null): void {
		if (value) {
			const parsed = this.phoneService.parsePhone(value);

			const isKnownPrefix = this.countryCodeOptions.some(
				(opt) => opt.value === parsed.prefix && opt.value !== 'other',
			);

			if (isKnownPrefix) {
				this.prefixControl.setValue(parsed.prefix, { emitEvent: false });
				this.showCustomPrefix = false;
				this.customPrefixControl.setValue('', { emitEvent: false });
			} else {
				this.prefixControl.setValue('other', { emitEvent: false });
				this.showCustomPrefix = true;
				this.customPrefixControl.setValue(parsed.prefix, { emitEvent: false });
			}

			const formatted = parsed.number ? this.phoneService.formatNumber(parsed.number, parsed.prefix) : '';
			this.phoneControl.setValue(formatted, { emitEvent: false });
		} else {
			this.prefixControl.setValue('+34', { emitEvent: false });
			this.phoneControl.setValue('', { emitEvent: false });
			this.customPrefixControl.setValue('', { emitEvent: false });
			this.showCustomPrefix = false;
		}
	}

	registerOnChange(fn: (value: string | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		if (isDisabled) {
			this.prefixControl.disable();
			this.phoneControl.disable();
			this.customPrefixControl.disable();
		} else {
			this.prefixControl.enable();
			this.phoneControl.enable();
			this.customPrefixControl.enable();
		}
	}

	onBlur(): void {
		this.onTouched();
	}
}
